import {
    AdminBaseRoles,
    Role,
} from '@deltek/specification-client/core/Configurations/Authorization'
import {
    CurrentUserInfo,
    CurrentUserInfoResponse,
} from '@deltek/specification-client/core/Models/currentUserInfo'
import { Firm } from '@deltek/specification-client/core/Models/firm'
import {
    createProductionOnlineHelpUrl,
    createTestingOnlineHelpUrl,
} from '@deltek/specification-client/core/Utilities/urlUtils'
import { useMemo } from 'react'

import {
    RouteDictionary,
    RouteInfo,
} from '@deltek/specification-client/core/Models/routeTypes'
import {
    isProduction,
    isStaging,
    processEnv,
} from 'components/configuration/environmentVariables'

type UseSidebarItemsProps = {
    userRole: string
    isBPM: boolean
    isDeltekUser: boolean
    isDeltekAdmin: boolean
    isDeltekAuthor: boolean
    isPublishingAuthor: boolean
    isDeltekReviewer: boolean
    hideIndustryAnalytics: boolean
    hasCompanyAccountAccess: boolean
}

/**
 * Get account type from currentUserInfo object
 */
export const getAccountType = (
    currentUserInfo: CurrentUserInfoResponse | undefined
) => {
    return currentUserInfo?.currentUserInfo?.account.accountType ?? ''
}

/**
 *  Generates online help URL based on account type and
 *  whether or not the app is in production mode.
 */
export const generateOnlineHelpUrl = (accountType: string = '') => {
    return isProduction || isStaging
        ? createProductionOnlineHelpUrl(accountType)
        : createTestingOnlineHelpUrl(accountType)
}

/**
 * Hook for getting online help URL
 */
export const useOnlineHelpUrl = (accountType: string = '') => {
    return useMemo(() => generateOnlineHelpUrl(accountType), [accountType])
}

export const isUserDesignatedAccountAdmin = (
    currentUserInfo: CurrentUserInfo,
    firm: Partial<Firm>
) => firm?.designatedAccountAdminUser?.email === currentUserInfo?.email

export const getHelpDeskEmailAddress = (
    currentUserInfo: CurrentUserInfo,
    firm: Partial<Firm>
) => {
    if (!isUserDesignatedAccountAdmin(currentUserInfo, firm))
        return firm?.designatedAccountAdminUser?.email!

    const supportContactEmail = 'SpecificationSupport@Deltek.com'
    return supportContactEmail
}
export const getRouteInfoFromPath = (path: string, routes: RouteDictionary) => {
    const pathSegments =
        path.match(/[^/]+/g)?.length! > 1
            ? [path.match(/[^/]+/g)?.[1]]
            : path.match(/[^/]+/g) ?? []
    let currentRouteInfo: RouteInfo | undefined
    let currentRouteList: RouteDictionary | undefined = routes

    for (let i = 0; i < pathSegments.length; i++) {
        const segment = `/bpm/${pathSegments[i]}`
        currentRouteInfo = currentRouteList?.[segment]
        currentRouteList = currentRouteInfo?.children

        // If the route has not been fully traversed yet
        // and there are no more children in the current node,
        // that means that the path provided is invalid.
        // Return null
        if (
            i < pathSegments.length - 1 &&
            (currentRouteList === undefined || currentRouteInfo === undefined)
        ) {
            return null
        }
    }

    return currentRouteInfo
}

export const useSidebarItems = ({
    userRole,
    isBPM,
    isDeltekUser,
    isDeltekAdmin,
    isDeltekAuthor,
    isPublishingAuthor,
    isDeltekReviewer,
    hideIndustryAnalytics,
    hasCompanyAccountAccess,
}: UseSidebarItemsProps) => {
    let bestPracticesChildren = [
        {
            label: 'Content Management',
            to: '/bpm/content-management',
            showNav: true,
        },
    ]

    if (!Number(processEnv.REACT_APP_ShowUpdatedProductListings)) {
        bestPracticesChildren.push({
            label: 'Product Card Management',
            to: '/bpm/product-card-management',
            showNav: isDeltekAdmin || isDeltekAuthor || isPublishingAuthor,
        })
    }

    return useMemo(
        () => [
            {
                label: 'Home',
                to: '/bpm/home',
                showNav: true,
            },

            {
                label: 'Products',
                to: '/bpm/products',
                showNav: true,
            },
            {
                label: 'Analytics',
                showNav: !!isBPM || !hideIndustryAnalytics,
                children: [
                    {
                        label: 'Industry Analytics',
                        to: '/bpm/analytics/home',
                        showNav: isBPM || isDeltekUser,
                    },
                    {
                        label: 'Legacy Analytics',
                        to: '/bpm/analytics/legacy',
                        showNav: !hideIndustryAnalytics,
                    },
                ],
            },
            {
                label: 'Company Accounts',
                to: '/bpm/company-accounts',
                showNav:
                    isDeltekAdmin ||
                    ((isDeltekAuthor || isDeltekReviewer) &&
                        hasCompanyAccountAccess),
            },
            {
                label: 'Best Practices',
                to: '/bpm/best-practices',
                showNav: isDeltekUser,
                children: bestPracticesChildren,
            },
            {
                label: 'Contacts',
                to: '/bpm/contacts',
                showNav: false,
            },

            {
                label: 'Settings',
                to: '/bpm/settings',
                showNav: AdminBaseRoles.includes(userRole as Role),
                children: [
                    { label: 'Account', to: '/bpm/account', showNav: true },
                    { label: 'Manage Users', to: '/bpm/users', showNav: true },
                ],
            },
        ],
        [
            userRole,
            isBPM,
            isDeltekUser,
            isDeltekAdmin,
            isDeltekAuthor,
            isPublishingAuthor,
            hideIndustryAnalytics,
            hasCompanyAccountAccess,
        ]
    )
}
